// Parses the ProLogs "Vehicle mileage report" CSV export (tab-separated with
// per-vehicle sections) and writes to ifta_state_miles. Each import is tagged
// with its own import_id / import_label so uploads from different companies
// stay separate in the UI. Re-importing with the same label replaces that
// label's prior rows; differently-labeled imports coexist.

import { supabase } from '../lib/supabase';

export interface ParsedTripRow {
  vehicleId: string;
  state: string;
  date: string; // YYYY-MM-DD
  startOdometer: number;
  endOdometer: number;
  distance: number;
}

export interface ImportPreview {
  totalRows: number;
  totalMiles: number;
  dateRange: { min: string; max: string } | null;
  matched: Array<{ vehicleId: string; truckId: string; unitNumber: string; rowCount: number; miles: number }>;
  unmatched: Array<{ vehicleId: string; rowCount: number; miles: number }>;
}

export interface ImportResult {
  success: boolean;
  error?: string;
  insertedRows?: number;
  truckCount?: number;
  unmatched?: string[];
}

function parseMDY(s: string): string | null {
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, mo, day, y] = m;
  return `${y}-${String(Number(mo)).padStart(2, '0')}-${String(Number(day)).padStart(2, '0')}`;
}

function normalizeUnit(u: string): string {
  const trimmed = u.trim();
  const stripped = trimmed.replace(/^0+/, '');
  return stripped || trimmed;
}

// Minimal CSV row parser that honors double-quoted cells and escaped quotes.
// Handles tab, comma, and semicolon as separator.
function splitCsvLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuotes) {
      if (c === '"') {
        if (line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        cur += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === sep) {
      out.push(cur);
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out.map(s => s.trim());
}

// Pick the separator that yields the most columns on average across the sample.
// ProLogs exports are usually tab-separated, but locale-based CSV exports can
// be comma or semicolon.
function detectSeparator(sampleLines: string[]): string {
  const candidates = ['\t', ',', ';'];
  let best = '\t';
  let bestScore = 0;
  for (const sep of candidates) {
    let score = 0;
    for (const line of sampleLines) {
      if (!line.trim()) continue;
      score += splitCsvLine(line, sep).length;
    }
    if (score > bestScore) {
      bestScore = score;
      best = sep;
    }
  }
  return best;
}

export function parseProLogsMileageCSV(text: string): ParsedTripRow[] {
  const rows: ParsedTripRow[] = [];
  // Handle CRLF, LF, and stand-alone CR (old Mac exports).
  const lines = text.split(/\r\n|\r|\n/);

  // Auto-detect separator from the first ~40 non-empty lines.
  const sample = lines.slice(0, 40).filter(l => l.trim());
  const sep = detectSeparator(sample);

  let currentVehicle: string | null = null;
  let currentState: string | null = null;

  for (const raw of lines) {
    if (!raw.trim()) continue;
    const cells = splitCsvLine(raw, sep);
    const first = cells[0];

    // Metadata lines — skip
    if (/^sep=/i.test(first)) continue;
    if (/^Vehicle mileage/i.test(first)) continue;

    // Vehicle section marker
    const vehMatch = first.match(/^Vehicle ID:\s*(.+)$/i);
    if (vehMatch) {
      currentVehicle = vehMatch[1].trim();
      currentState = null;
      continue;
    }

    // Column header row (repeats per vehicle)
    if (/^state$/i.test(first) && /^date$/i.test(cells[1] || '')) continue;

    // Totals rows (appear anywhere in the row depending on locale)
    if (cells.some(c => /^Total mileage/i.test(c))) continue;

    // State header: 2-letter code alone (other cells empty)
    if (/^[A-Z]{2}$/.test(first) && cells.slice(1).every(c => !c)) {
      currentState = first;
      continue;
    }

    // Trip row: first col empty, then date / start odo / end odo / distance
    if (!first && cells[1] && currentVehicle && currentState) {
      const date = parseMDY(cells[1]);
      const startOdo = parseFloat(cells[2] || '');
      const endOdo = parseFloat(cells[3] || '');
      const distance = parseFloat((cells[4] || '').replace(/,/g, ''));
      if (!date || isNaN(distance)) continue;
      rows.push({
        vehicleId: currentVehicle,
        state: currentState,
        date,
        startOdometer: isNaN(startOdo) ? 0 : startOdo,
        endOdometer: isNaN(endOdo) ? 0 : endOdo,
        distance,
      });
    }
  }

  // Diagnostic so we can see what the parser saw if it returns nothing.
  if (rows.length === 0) {
    const preview = lines.slice(0, 20).map((l, i) => `${i}: ${JSON.stringify(l)}`).join('\n');
    // eslint-disable-next-line no-console
    console.warn(
      `[prologsIftaCsv] Parsed 0 rows. Detected separator: ${JSON.stringify(sep)}.\nFirst 20 lines:\n${preview}`
    );
  }

  return rows;
}

// Combines rows parsed from several CSV files (e.g. one ProLogs export per
// truck) into a single import. A row that is identical to one already taken
// from a *different* file is dropped, so overlapping exports don't double the
// miles. Identical rows inside the same file are kept as-is.
export function mergeParsedFiles(
  files: ParsedTripRow[][]
): { rows: ParsedTripRow[]; duplicateRows: number } {
  // key → index of the file that first contributed it
  const seen = new Map<string, number>();
  const rows: ParsedTripRow[] = [];
  let duplicateRows = 0;
  files.forEach((fileRows, fileIdx) => {
    for (const r of fileRows) {
      const key = [r.vehicleId, r.state, r.date, r.startOdometer, r.endOdometer, r.distance].join('|');
      const owner = seen.get(key);
      if (owner !== undefined && owner !== fileIdx) {
        duplicateRows++;
        continue;
      }
      seen.set(key, fileIdx);
      rows.push(r);
    }
  });
  return { rows, duplicateRows };
}

export async function buildImportPreview(parsed: ParsedTripRow[]): Promise<ImportPreview> {
  if (parsed.length === 0) {
    return { totalRows: 0, totalMiles: 0, dateRange: null, matched: [], unmatched: [] };
  }

  // Pull every truck once; try exact match, then leading-zero-stripped fallback
  // so ProLogs "0330" also matches a TMS "330" if the user stored it that way.
  const { data: trucks, error } = await supabase.from('trucks').select('id, unit_number');
  if (error) throw error;

  const exact = new Map<string, { id: string; unit: string }>();
  const normalized = new Map<string, { id: string; unit: string }>();
  for (const t of trucks || []) {
    exact.set(t.unit_number, { id: t.id, unit: t.unit_number });
    normalized.set(normalizeUnit(t.unit_number), { id: t.id, unit: t.unit_number });
  }

  // Aggregate by vehicleId for the preview summary
  const perVehicle = new Map<string, { count: number; miles: number }>();
  for (const r of parsed) {
    const a = perVehicle.get(r.vehicleId) || { count: 0, miles: 0 };
    a.count++;
    a.miles += r.distance;
    perVehicle.set(r.vehicleId, a);
  }

  const matched: ImportPreview['matched'] = [];
  const unmatched: ImportPreview['unmatched'] = [];
  for (const [vid, agg] of perVehicle) {
    const hit = exact.get(vid) || normalized.get(normalizeUnit(vid));
    if (hit) {
      matched.push({
        vehicleId: vid,
        truckId: hit.id,
        unitNumber: hit.unit,
        rowCount: agg.count,
        miles: agg.miles,
      });
    } else {
      unmatched.push({ vehicleId: vid, rowCount: agg.count, miles: agg.miles });
    }
  }

  const dates = parsed.map(r => r.date).sort();
  const dateRange = { min: dates[0], max: dates[dates.length - 1] };
  const totalMiles = parsed.reduce((s, r) => s + r.distance, 0);

  return { totalRows: parsed.length, totalMiles, dateRange, matched, unmatched };
}

export async function importProLogsCSV(
  parsed: ParsedTripRow[],
  preview: ImportPreview,
  importLabel: string
): Promise<ImportResult> {
  if (parsed.length === 0 || !preview.dateRange) {
    return { success: false, error: 'No rows to import' };
  }
  const label = importLabel.trim();
  if (!label) {
    return { success: false, error: 'Import name is required' };
  }

  try {
    // vehicleId → truck_id. Vehicles not in this map still get imported, but
    // with truck_id=null and vehicle_id preserved so the CSV totals are faithful
    // even when the truck isn't in the TMS fleet yet.
    const vidToTid = new Map<string, string>();
    for (const m of preview.matched) vidToTid.set(m.vehicleId, m.truckId);

    // Aggregate per (truck_id|vehicleId, date, state). CSV has one row per
    // trip; DB is per (truck, date, state, source), so sum trip distances.
    const agg = new Map<string, {
      truck_id: string | null;
      vehicle_id: string;
      trip_date: string;
      jurisdiction_code: string;
      miles: number;
    }>();
    for (const r of parsed) {
      const tid = vidToTid.get(r.vehicleId) || null;
      const key = `${tid || 'vid:' + r.vehicleId}|${r.date}|${r.state}`;
      const e = agg.get(key);
      if (e) {
        e.miles += r.distance;
      } else {
        agg.set(key, {
          truck_id: tid,
          vehicle_id: r.vehicleId,
          trip_date: r.date,
          jurisdiction_code: r.state,
          miles: r.distance,
        });
      }
    }

    const importId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const importUploadedAt = new Date().toISOString();

    const insertRows = Array.from(agg.values()).map(a => ({
      truck_id: a.truck_id,
      vehicle_id: a.vehicle_id,
      trip_date: a.trip_date,
      jurisdiction_code: a.jurisdiction_code,
      miles: Math.round(a.miles * 100) / 100,
      source: 'prologs_csv',
      import_id: importId,
      import_label: label,
      import_uploaded_at: importUploadedAt,
    }));

    // Replace any prior import with the same label (so re-uploading a file
    // with the same name is a refresh). Differently-labeled imports — e.g.
    // from a different company — are left alone.
    const { error: delErr } = await supabase
      .from('ifta_state_miles')
      .delete()
      .eq('import_label', label);
    if (delErr) throw delErr;

    // Insert in batches (Supabase rejects very large single inserts).
    const batchSize = 500;
    let inserted = 0;
    for (let i = 0; i < insertRows.length; i += batchSize) {
      const batch = insertRows.slice(i, i + batchSize);
      const { error: insErr } = await supabase.from('ifta_state_miles').insert(batch);
      if (insErr) throw insErr;
      inserted += batch.length;
    }

    // Count distinct vehicles (matched + unmatched) to reflect what actually
    // landed in the DB, since unmatched rows are no longer dropped.
    const distinctVehicles = new Set<string>([
      ...preview.matched.map(m => m.vehicleId),
      ...preview.unmatched.map(u => u.vehicleId),
    ]);
    return {
      success: true,
      insertedRows: inserted,
      truckCount: distinctVehicles.size,
      unmatched: preview.unmatched.map(u => u.vehicleId),
    };
  } catch (err: any) {
    return { success: false, error: err?.message || 'CSV import failed' };
  }
}

export async function renameIftaImport(
  importId: string,
  newLabel: string
): Promise<{ success: boolean; error?: string }> {
  const label = newLabel.trim();
  if (!label) return { success: false, error: 'Name cannot be empty' };
  try {
    const { error } = await supabase
      .from('ifta_state_miles')
      .update({ import_label: label })
      .eq('import_id', importId);
    if (error) throw error;
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Rename failed' };
  }
}

export async function deleteIftaImport(
  importId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('ifta_state_miles')
      .delete()
      .eq('import_id', importId);
    if (error) throw error;
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Delete failed' };
  }
}

// Wipes every ifta_state_miles row that has no import_id — i.e. rows that
// landed via the old compute path before CSV-only mode. Used by the "Pre-CSV
// data" card's delete button so the user can clean up legacy residuals.
export async function deleteIftaLegacyRows(): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('ifta_state_miles')
      .delete()
      .is('import_id', null);
    if (error) throw error;
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Delete failed' };
  }
}
